import type { ReactNode } from 'react';
import { Link } from 'react-router';
import {
  cx,
  DashboardRow,
  ExpandableText,
  STATUS_INK,
  type StatusRole,
  StatusSymbol,
  useIsDesktop,
} from '../../../ui';
import { clientHref, clientsHref, FULFILLMENT } from '../shared';
import { DashboardColumn } from './DashboardColumn';
import {
  daysSince,
  dayWords,
  FOCUS_VISIBLE_ROWS,
  type FocusLists,
  type FocusRow,
  firstName,
  firstReason,
  lateStepOfReason,
  nameOf,
  wroteWords,
} from './model';

/**
 * CLIENTS TO FOCUS ON (brief section 5; mockup `dashboard-v2-side.html:178-235`).
 *
 * WHICH VIEW IS THE PAGE'S: the two pick tiles above choose it and keep it in the URL
 * (`?focus=yellow`), so this column only draws the chosen one. The red view is three lists -
 * Waiting for our reply, Gone quiet and Needs attention for the red clients whose reason is
 * neither (Q-DB-1) - and the yellow view is one list. Gone quiet and Needs attention are drawn
 * only when they hold a client (R4); Waiting keeps its empty sentence, as the mockup draws it.
 *
 * EVERY ROW OPENS THE CLIENT PAGE (no "Open group", D7): FE-FOUNDATION's redesigned
 * `DashboardRow`, whose client name is a link stretched over the whole row - status symbol,
 * name with the package as plain text (L2), owner, days, chevron, and a second line whose
 * quote or reason draws its own "More" when it is cut. Its actions - "Mark as handled" on a
 * Waiting row, the pause on every row (Q-DB-2) - sit outside the link, visible at rest (D2,
 * L11).
 */
export type Focus = 'red' | 'yellow';
export type FocusKind = 'waiting' | 'goneQuiet' | 'other' | 'yellow';

export interface FocusClientsProps {
  readonly focus: Focus;
  readonly lists: FocusLists;
  readonly today: string;
  /** The Telegram data age at the right of the heading (D9). */
  readonly age?: ReactNode;
  /** The stale notice with "Retry now", under the heading when Telegram is stale (D9, D10). */
  readonly notice?: ReactNode;
  /** The row's actions, outside its link. */
  readonly actions?: ((row: FocusRow, kind: FocusKind) => ReactNode) | undefined;
}

/** The two views' words are the configured health levels' (F9, priority 7). */
const VIEW: Readonly<Record<Focus, { readonly status: StatusRole; readonly word: string }>> = {
  red: { status: 'act-now', word: FULFILLMENT.health.levels.red },
  yellow: { status: 'watch', word: FULFILLMENT.health.levels.yellow },
};

export function FocusClients({ focus, lists, today, age, notice, actions }: FocusClientsProps) {
  const isDesktop = useIsDesktop();
  const view = VIEW[focus];
  const title = 'Clients to focus on';
  /** The chosen tile's symbol and word (L2: symbol plus word, no capsule). */
  const adornment = (
    <span
      className="flex items-center gap-1.5 text-sm font-semibold text-ink"
      data-focus-view={focus}
    >
      <StatusSymbol status={view.status} />
      {view.word}
    </span>
  );
  const body = (
    <div className="pb-2" data-focus-body="">
      {notice !== undefined && <div className="pb-2">{notice}</div>}
      {focus === 'red' ? (
        <RedView lists={lists} today={today} actions={actions} />
      ) : (
        <YellowView rows={lists.yellow} today={today} actions={actions} />
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <DashboardColumn name="focus" title={title} adornment={adornment} trailing={age}>
        {body}
      </DashboardColumn>
    );
  }
  return (
    <section aria-label={title} data-dashboard-section="focus">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-2">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {adornment}
        {age !== undefined && <span className="ms-auto">{age}</span>}
      </header>
      {body}
    </section>
  );
}

function RedView({
  lists,
  today,
  actions,
}: {
  readonly lists: FocusLists;
  readonly today: string;
  readonly actions: FocusClientsProps['actions'];
}) {
  return (
    <>
      <Subhead
        title="Waiting for our reply"
        hint={`· ${String(lists.waiting.length)} · they wrote, nobody answered yet`}
      />
      {lists.waiting.length === 0 ? (
        <p className="border-t border-line-subtle py-2.5 text-sm text-ink-subtle">
          Nobody is waiting for our reply.
        </p>
      ) : (
        <Rows label="Waiting for our reply">
          {lists.waiting.map((row) => (
            <ClientRow
              key={row.enrollment.id}
              row={row}
              kind="waiting"
              today={today}
              actions={actions}
            />
          ))}
        </Rows>
      )}

      {/* R4: a list with nothing in it is not drawn; only Waiting keeps its empty sentence. */}
      {lists.goneQuiet.length > 0 && (
        <>
          <Subhead
            title="Gone quiet"
            hint={`· ${String(lists.goneQuiet.length)} · no message from the client`}
          />
          <Rows label="Gone quiet">
            {lists.goneQuiet.slice(0, FOCUS_VISIBLE_ROWS).map((row) => (
              <ClientRow
                key={row.enrollment.id}
                row={row}
                kind="goneQuiet"
                today={today}
                actions={actions}
              />
            ))}
          </Rows>
        </>
      )}
      {lists.goneQuiet.length > FOCUS_VISIBLE_ROWS && (
        <ShowAll count={lists.goneQuiet.length} to={clientsHref({ health: 'red' })} />
      )}

      {lists.other.length > 0 && (
        <>
          <Subhead title={FULFILLMENT.health.levels.red} hint={`· ${String(lists.other.length)}`} />
          <Rows label={FULFILLMENT.health.levels.red}>
            {lists.other.map((row) => (
              <ClientRow
                key={row.enrollment.id}
                row={row}
                kind="other"
                today={today}
                actions={actions}
              />
            ))}
          </Rows>
        </>
      )}
    </>
  );
}

function YellowView({
  rows,
  today,
  actions,
}: {
  readonly rows: readonly FocusRow[];
  readonly today: string;
  readonly actions: FocusClientsProps['actions'];
}) {
  if (rows.length === 0) {
    return <p className="pt-3 text-sm text-ink-subtle">No yellow clients.</p>;
  }
  return (
    <>
      <Rows label={FULFILLMENT.health.levels.yellow} className="mt-3">
        {rows.slice(0, FOCUS_VISIBLE_ROWS).map((row) => (
          <ClientRow
            key={row.enrollment.id}
            row={row}
            kind="yellow"
            today={today}
            actions={actions}
          />
        ))}
      </Rows>
      {rows.length > FOCUS_VISIBLE_ROWS && (
        <ShowAll count={rows.length} to={clientsHref({ health: 'yellow' })} />
      )}
    </>
  );
}

/** A list's subhead (mockup `.subhead`): the name at 13 semibold, the hint after it. */
function Subhead({ title, hint }: { readonly title: string; readonly hint: string }) {
  return (
    <h3 className="flex flex-wrap items-baseline gap-x-2 pt-4 pb-1.5 text-2xs font-semibold text-ink-muted">
      {title}
      <span className="font-normal text-ink-subtle">{hint}</span>
    </h3>
  );
}

/** Hairlines between the rows and above the first, as the mockup's `.rows`. */
function Rows({
  label,
  className,
  children,
}: {
  readonly label: string;
  readonly className?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <ul
      aria-label={label}
      className={cx('divide-y divide-line-subtle border-y border-line-subtle', className)}
    >
      {children}
    </ul>
  );
}

function ShowAll({ count, to }: { readonly count: number; readonly to: string }) {
  return (
    <Link to={to} className="mt-2.5 inline-block text-sm text-accent">
      Show all {String(count)}
    </Link>
  );
}

/**
 * ONE CLIENT ROW (mockup `:197-217`, `:224-230`): the status symbol, the name with the package
 * after it, the owner's first name, the right column and the chevron on the first line; the
 * quote (a Waiting row) or the first reason (Needs attention, Keep an eye on) on the second,
 * cut at two lines with "More" under it.
 */
function ClientRow({
  row,
  kind,
  today,
  actions,
}: {
  readonly row: FocusRow;
  readonly kind: FocusKind;
  readonly today: string;
  readonly actions: FocusClientsProps['actions'];
}) {
  const agency = row.agency;
  const second = secondLine(row, kind);
  const own = actions?.(row, kind);
  return (
    <DashboardRow
      {...(agency === undefined ? {} : { to: clientHref(agency.id) })}
      status={<StatusSymbol status={kind === 'yellow' ? 'watch' : 'act-now'} />}
      title={<span className="font-semibold">{nameOf(row)}</span>}
      {...(row.enrollment.tier === null
        ? {}
        : { trailing: <span className="text-2xs text-ink-muted">{row.enrollment.tier}</span> })}
      owner={firstName(agency?.csmName ?? null) ?? ''}
      days={rightColumn(row, kind, today)}
      chevron
      {...(second === null
        ? {}
        : {
            secondary: (
              <ExpandableText
                lines={2}
                subject={nameOf(row)}
                className={kind === 'waiting' ? 'italic' : undefined}
              >
                {second}
              </ExpandableText>
            ),
          })}
      {...(own === null || own === undefined ? {} : { actions: own })}
    />
  );
}

/** The days column: how long they wait, how long they are quiet, how late, or when they wrote. */
function rightColumn(row: FocusRow, kind: FocusKind, today: string): ReactNode {
  const agency = row.agency;
  if (kind === 'waiting') {
    const days = daysSince(agency?.unansweredSince ?? null, today);
    return days === null ? null : <span className={STATUS_INK['act-now']}>{dayWords(days)}</span>;
  }
  if (kind === 'goneQuiet') {
    const days = daysSince(agency?.lastClientMessageAt ?? null, today);
    return days === null ? null : <span className="text-ink">{dayWords(days)}</span>;
  }
  if (kind === 'yellow') {
    const step = lateStepOfReason(row.enrollment);
    if (step !== null && step.daysLate !== null) {
      return (
        <span className={cx('inline-flex items-center gap-1', STATUS_INK.late)}>
          <StatusSymbol status="late" />
          {`${dayWords(step.daysLate)} late`}
        </span>
      );
    }
  }
  /** Q-DB-3: a reason that is not a late step shows when the client last wrote. */
  const wrote = wroteWords(agency?.lastClientMessageAt ?? null);
  return wrote === null ? null : (
    <span className="text-2xs font-normal text-ink-muted">{wrote}</span>
  );
}

function secondLine(row: FocusRow, kind: FocusKind): string | null {
  if (kind === 'waiting') {
    const excerpt = row.agency?.unansweredExcerpt ?? null;
    return excerpt === null ? null : `“${excerpt}”`;
  }
  if (kind === 'goneQuiet') return null;
  return firstReason(row.enrollment.healthReason);
}
